<script setup lang="ts">
import { Input, SingleSelectList, SingleSelectListItem } from '@laam/ui/base';
import { PhMagnifyingGlass, PhX } from '@phosphor-icons/vue';
import CarouselColumn from '~/components/brands/CarouselColumn.vue';
import { useFetchCategories } from '~/data/entities';
import {
	getFilteredList,
	getGroupByInitial,
} from '~/components/catalogue/utils';
import { useBrands } from '~/data/brands';
import CartButton from '~/components/CartButton.vue';
import BrandCategories from '~/components/brands/BrandCategories.vue';
import BrandListingLoader from '~/components/brands/BrandListingLoader.vue';
import BrandsList from '~/components/brands/BrandsList.vue';
import { useDebounceFn } from '@vueuse/core';
import { useRoute } from 'vue-router';
import type { BannerReasons } from '~/components/TopBanner.vue';
import type { ShippingConfig } from '~/types/statsig';
const route = useRoute();
const { $eventClient, $cathodeClient } = useNuxtApp();

const searchQuery = ref('');

const { data: categories } = useFetchCategories();

const selectedCategory = ref({
	id: 1,
	title: 'Women',
});

const l1Categories = computed(() => {
	if (!categories.value) return [];
	return categories.value.map((category) => ({
		id: category.id,
		title: category.title,
	}));
});

const activePill = ref<number | undefined>(undefined);

const sortOptionSelected = ref('ALPHABETICAL');

const {
	data: brands,
	brandsLoading,
	waitForBrands,
} = useBrands({
	enabled: ref(true),
	filters: computed(() => ({
		ranking: sortOptionSelected.value,
		l1Category: selectedCategory.value.id,
		l3Category: activePill.value,
		limit: undefined,
	})),
});
onServerPrefetch(async () => {
	await waitForBrands();
});

const l3For = ref('Clothing');

const pills = computed(() => {
	if (!categories.value || !selectedCategory.value || !l3For.value) return [];

	const selectedTitle = selectedCategory.value.title.toLowerCase();
	const l3ForTitle = l3For.value.toLowerCase();

	const l1Category = categories.value.find(
		(category) => category.title.toLowerCase() === selectedTitle,
	);
	if (!l1Category?.children) return [];

	const clothingCategory = l1Category.children.find(
		(category) => category.title.toLowerCase() === l3ForTitle,
	);

	const clothingL3 = clothingCategory?.children?.map((c) => c) ?? [];

	const l2Category = l1Category.children
		.filter((category) => category.title.toLowerCase() !== l3ForTitle)
		.map((category) => category);

	return [...clothingL3, ...l2Category];
});

const selectedGroup = ref('');
const debouncedSearchQuery = ref('');

const updateDebounced = useDebounceFn((value: string) => {
	debouncedSearchQuery.value = value;
}, 300);

watch(searchQuery, (newVal) => {
	updateDebounced(newVal);
});
const filteredItemsList = computed(() =>
	getFilteredList(brands.value, debouncedSearchQuery.value, 'name'),
);
const initialsGroup = computed(() => {
	if (showAlphabets.value) {
		return getGroupByInitial(filteredItemsList.value, 'name', true);
	} else {
		return [];
	}
});
function groupedList(initial: string) {
	const list = filteredItemsList.value.filter((brand) => {
		const name = brand.name?.toLowerCase()?.trim() || '';
		if (initial === '#') {
			return /^[0-9]/.test(name);
		}
		return name.startsWith(initial.toLowerCase());
	});
	return list;
}

function checkSticky(state: boolean, brandInitial: string) {
	if (state) {
		selectedGroup.value = brandInitial;
	}
}

function handleChange(value: string) {
	const element = document.getElementById(value);
	if (element) {
		const offset = isMobileView() ? -140 : -380;
		const elementPosition = element.getBoundingClientRect().top;
		const offsetPosition = elementPosition + window.pageYOffset + offset;

		window.scrollTo({
			top: offsetPosition,
			behavior: 'instant',
		});
	}
	selectedGroup.value = value;
}
const handlePills = (pillId: number) => {
	if (!activePill.value) {
		if (activePill.value === pillId) {
			activePill.value = undefined;
			return;
		}
	} else if (activePill.value === pillId) {
		activePill.value = undefined;
		return;
	}
	activePill.value = pillId;
};
const handlel1CategoryClick = (categoryId: number, categoryTitle: string) => {
	selectedCategory.value.id = categoryId;
	selectedCategory.value.title = categoryTitle;
	activePill.value = undefined;
	sortOptionSelected.value = 'ALPHABETICAL';
};
const updateSortOption = (value: string) => {
	sortOptionSelected.value = value;
};
provide('updateSortOption', updateSortOption);
const y = ref(0);
onMounted(() => {
	window.addEventListener('scroll', () => {
		y.value = window.scrollY;
	});
});
const handleClick = (
	brandName: string,
	brandHandle: string,
	image: string,
	verticalIndex: number,
	productCount: number,
	carousel?: string,
) => {
	$cathodeClient.queueEvent('entity_click', {
		source: window.location.href,
		page: 'brands',
		href: brandHandle || '',
		clicked_from: isMobileView() ? 'homepage' : 'brand-listing',
		entity: 'brand',
		image: image || '',
		horizontal_index: 0,
		vertical_index: verticalIndex,
		title: brandName,
		slotted: false,
		heading: '',
		component: '',
		product_count: productCount || null,
		sale_percentage: 0,
		scroll_position: y.value,
		data_source: `${brands?.value?.[verticalIndex]?.ranking_scope}-${brands?.value?.[verticalIndex]?.ranking_version}`,
		data_string: '',
	});

	$eventClient.sendEvent('brand-click', {
		brandName: brandName,
		brandHandle: brandHandle,
		from: window.location.href,
		nodeIdFilter: activePill.value ?? undefined,
		carousel: carousel ?? '',
	});
};

const brandsListContainer = ref<HTMLElement | null>(null);
const show = ref(false);
const top = ref(0);
const checkVisibility = () => {
	if (brandsListContainer.value) {
		const rect = brandsListContainer.value.getBoundingClientRect();
		top.value = rect.top;
		show.value = rect.top <= 200 && rect.bottom >= 0;
	}
};

onMounted(() => {
	window.addEventListener('scroll', checkVisibility, { passive: true });
	checkVisibility();
});

onBeforeUnmount(() => {
	window.removeEventListener('scroll', checkVisibility);
});
const updateSelectedGroup = (brandInitial: string) => {
	const element = document.getElementById(brandInitial);

	if (element) {
		const rect = element.getBoundingClientRect();
		const isVisible =
			rect.top >= 0 &&
			rect.left >= 0 &&
			rect.bottom <=
				(window.innerHeight || document.documentElement.clientHeight) &&
			rect.right <= (window.innerWidth || document.documentElement.clientWidth);

		if (isVisible) {
			selectedGroup.value = brandInitial;
		}
	}
};
const showAlphabets = computed(
	() => sortOptionSelected.value === 'ALPHABETICAL',
);
const isMobileBrandsPage = computed(() => {
	return route.name === 'brands' && isMobileView();
});
const topBannerReason = inject('topBannerReason') as Ref<
	BannerReasons | undefined
>;
const shippingConfig = inject('shippingConfig') as Ref<ShippingConfig>;
const showingTopBanner = computed(() => {
	return !!topBannerReason.value && shippingConfig.value !== null;
});
provide('handleClick', handleClick);
provide('checkSticky', checkSticky);
provide('groupedList', groupedList);
provide('brands', filteredItemsList);
provide('updateSelectedGroup', updateSelectedGroup);
</script>
<template>
	<div>
		<div v-if="isMobileView()">
			<div
				class="category-list-header fixed z-50 w-full bg-white"
				:class="{
					'top-[23px]': isMobileBrandsPage && showingTopBanner,
					'top-0': !isMobileBrandsPage || !showingTopBanner,
				}"
			>
				<div class="flex py-md pr-md pl-3xl gap-xs bg-white">
					<Input
						v-model="searchQuery"
						size="sm"
						class="flex-1 bg-gray-100"
						placeholder="Search for brands"
					>
						<template #leftIcon><PhMagnifyingGlass /></template>
						<template v-if="searchQuery" #rightIcon>
							<PhX
								size="20"
								class="hover:cursor-pointer"
								@click="searchQuery = ''"
							/>
						</template>
					</Input>
					<CartButton />
				</div>
			</div>
			<div class="bg-gray-200 gap-md flex flex-col mt-[50px]">
				<CarouselColumn :hide-columns="searchQuery != ''" />
				<div class="relative">
					<!-- Sticky Header Section -->
					<div
						ref="stickyHeader"
						class="flex flex-col bg-white gap-3xl pt-3xl pb-md px-3xl sticky top-0"
						:class="{
							'top-[23px]': isMobileBrandsPage && showingTopBanner,
							'top-0': !isMobileBrandsPage || !showingTopBanner,
						}"
					>
						<p class="text-gray-900 text-lg font-semibold">All brands</p>
						<BrandCategories
							:l1-categories="l1Categories"
							:selected-category="selectedCategory"
							:active-pill="activePill"
							:sort-option-selected="sortOptionSelected"
							:pills="pills"
							@handle-pills="handlePills"
							@handlel1-category-click="handlel1CategoryClick"
						/>
					</div>
					<div
						v-if="!brandsLoading"
						ref="brandsListContainer"
						class="flex flex-col p-3xl bg-white"
						:class="showAlphabets ? 'gap-3xl' : 'gap-none'"
					>
						<Transition name="fade-slide" appear>
							<div v-if="showAlphabets && show">
								<SingleSelectList
									class="flex pt-2xl px-xs flex-col justify-start bg-gray-100 rounded-full h-[60%] overflow-y-auto no-scroll fixed top-[200px] right-md pb-xl"
									default-value="recommended"
									:model-value="selectedGroup"
									@update:model-value="handleChange"
								>
									<SingleSelectListItem
										v-for="brandInitial in initialsGroup"
										:id="`single-select-${brandInitial}`"
										:key="brandInitial"
										:value="brandInitial"
										class="!px-sm !py-0 flex flex-col items-center justify-center gap-xxs !h-3xl data-[state=checked]:bg-gray-100"
									>
										<p
											class="text-xs font-semibold text-center"
											:class="{
												'text-gray-800 ': selectedGroup === brandInitial,
												'text-gray-400 ': selectedGroup !== brandInitial,
											}"
										>
											{{ brandInitial }}
										</p>
									</SingleSelectListItem>
								</SingleSelectList>
							</div>
						</Transition>

						<BrandsList
							:initials-group="initialsGroup"
							:active-pill="activePill"
							:show-alphabets="showAlphabets"
						/>
					</div>
					<BrandListingLoader v-else />
				</div>
			</div>
		</div>
		<div v-if="!isMobileView()" class="flex flex-col lg:pt-7xl lg:gap-7xl">
			<CarouselColumn />
			<div class="relative">
				<!-- Sticky Header Section -->
				<div
					class="sticky z-10 bg-white flex flex-col gap-3xl pt-7xl pb-3xl"
					:class="showingTopBanner ? 'top-[103px]' : 'top-[80px]'"
				>
					<!-- Title and Search -->
					<div class="flex items-center gap-3xl justify-between">
						<p class="text-gray-700 text-2xl font-semibold">All Brands</p>
						<Input
							v-model="searchQuery"
							size="sm"
							class="w-[50%] bg-gray-100"
							placeholder="Search for brands"
						>
							<template #leftIcon><PhMagnifyingGlass /></template>
							<template v-if="searchQuery" #rightIcon>
								<PhX
									size="20"
									class="hover:cursor-pointer"
									@click="searchQuery = ''"
								/>
							</template>
						</Input>
					</div>
					<BrandCategories
						:l1-categories="l1Categories"
						:selected-category="selectedCategory"
						:active-pill="activePill"
						:sort-option-selected="sortOptionSelected"
						:pills="pills"
						@handle-pills="handlePills"
						@handlel1-category-click="handlel1CategoryClick"
					/>

					<!-- Alphabetical Filter -->
					<SingleSelectList
						v-if="showAlphabets"
						class="pb-3xl flex justify-start w-full flex-row overflow-x-scroll no-scroll border-b border-gray-200"
						default-value="recommended"
						:model-value="selectedGroup"
						@update:model-value="handleChange"
					>
						<SingleSelectListItem
							v-for="brandInitial in initialsGroup"
							:id="`single-select-${brandInitial}`"
							:key="brandInitial"
							:value="brandInitial"
							class="flex flex-col items-center rounded-md h-8xl w-8xl !p-sm text-gray-800 bg-white data-[state=checked]:text-white data-[state=checked]:bg-gray-800 text-sm font-semibold"
						>
							{{ brandInitial }}
						</SingleSelectListItem>
					</SingleSelectList>
				</div>

				<div
					v-if="!brandsLoading"
					class="brands-list-container flex p-3xl flex-col gap-3xl"
				>
					<BrandsList
						:initials-group="initialsGroup"
						:active-pill="activePill"
						:show-alphabets="showAlphabets"
					/>
				</div>
				<BrandListingLoader v-else />
			</div>
		</div>
	</div>
</template>

<style scoped>
.no-scroll {
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none; /* For Firefox */
}

.no-scroll::-webkit-scrollbar {
	display: none; /* For Webkit browsers */
}
.fade-slide-enter-from,
.fade-slide-leave-to {
	opacity: 0;
	transform: translateY(10px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
	opacity: 1;
	transform: translateY(0);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
	transition: all ease;
}
</style>
